import { useSafeTheme } from '@/theme/hooks'
import { isAxiosError } from 'axios'
import type mapboxgl from 'mapbox-gl'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { flyMapToLngLat as animateMapToLngLat } from '../Components/SmartSearch/map/flyToFeature'
import {
  focusSmartSearchHit,
  restoreOperationalLayerVisibility,
  snapshotOperationalLayerVisibility,
  type OperationalLayerVisibilitySnapshot,
} from '../Components/SmartSearch/map/focusSearchHit'
import { removeSmartSearchHitPopup } from '../Components/SmartSearch/map/smartSearchHitPopup'
import {
  removeSmartSearchNavigationPin,
  setSmartSearchNavigationPin,
} from '../Components/SmartSearch/map/smartSearchNavigationPin'
import { buildGroupsFromServerHits } from '../Components/SmartSearch/search/buildGroupsFromServerHits'
import { buildLayerSearchRows } from '../Components/SmartSearch/search/buildLayerSearchRows'
import { type SmartSearchLayerGroup } from '../Components/SmartSearch/search/buildUnifiedSearchGroups'
import type { LayerSearchFacetFilters } from '../Components/SmartSearch/search/filterLayerSearchIndex'
import {
  hasPositiveTerms,
  parseAttributeQuery,
} from '../Components/SmartSearch/search/parseAttributeQuery'
import { serverHitToFeatureHit } from '../Components/SmartSearch/search/serverHitToFeatureHit'
import type {
  OperationalLayerRow,
  SmartSearchFeatureHit,
  SmartSearchHit,
  SmartSearchMessageHit,
  SmartSearchRasterHit,
} from '../Components/SmartSearch/types'
import { restoreStationExplorationView } from '../Modules/CenterMap'
import {
  globalSearch,
  type GlobalSearchKind,
  type RasterSource,
  type RasterType,
} from '../api/globalSearchApi'
import {
  getLayerFeaturesGeojson,
  getOperationalLayersByResort,
} from '../api/operationalLayerApi'
import { dispatchGisRasterFlyTo } from '../hooks/map/useGisRasterMapLayers'
import { dispatchLayerFlyTo } from '../utils/layerFlyToEvent'

import { useAuth } from './AuthContext'

export type SmartSearchIndexStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'forbidden'
  | 'no_permission'

export type FeatureSearchStatus = 'idle' | 'loading' | 'ready' | 'error'

export type SmartSearchFilters = {
  types: GlobalSearchKind[]
  createdAfter: string | null
  createdBefore: string | null
  creatorId: number | null
  rasterSources: RasterSource[]
  rasterTypes: RasterType[]
}

export const DEFAULT_SMART_SEARCH_FILTERS: SmartSearchFilters = {
  types: ['vector_feature', 'vector_layer', 'raster', 'message'],
  createdAfter: null,
  createdBefore: null,
  creatorId: null,
  rasterSources: [],
  rasterTypes: [],
}

// true when filters narrow the search beyond the defaults.
export const areFiltersActive = (f: SmartSearchFilters): boolean =>
  f.types.length !== DEFAULT_SMART_SEARCH_FILTERS.types.length ||
  f.createdAfter != null ||
  f.createdBefore != null ||
  f.creatorId != null ||
  f.rasterSources.length > 0 ||
  f.rasterTypes.length > 0

type GlobalSearchState = {
  query: string
  vectorFeatureHits: Array<{
    feature_id: string | number
    layer_id: number
    geometry: GeoJSON.Geometry
    properties?: Record<string, unknown> | null
  }>
  rasterHits: SmartSearchRasterHit[]
  messageHits: SmartSearchMessageHit[]
  totals: Record<GlobalSearchKind, number>
  status: FeatureSearchStatus
}

const EMPTY_TOTALS: Record<GlobalSearchKind, number> = {
  vector_feature: 0,
  vector_layer: 0,
  raster: 0,
  message: 0,
}

export type SmartSearchContextValue = {
  registerMap: (map: mapboxgl.Map | null) => void
  paletteOpen: boolean
  openPalette: () => void
  closePalette: () => void
  togglePalette: () => void
  indexStatus: SmartSearchIndexStatus
  refreshIndex: () => Promise<void>
  layerCategoryFilter: LayerSearchFacetFilters['category']
  setLayerCategoryFilter: (v: LayerSearchFacetFilters['category']) => void
  folderFacetLabels: string[]
  hasLayersWithoutFolder: boolean
  searchLayerGroups: (query: string) => SmartSearchLayerGroup[]
  getLayerFeatureCount: (layerId: number) => number
  getLayerName: (layerId: number) => string | null
  getLayerPropertiesOrder: (layerId: number) => string[] | null
  listFeatureHitsForLayer: (layerId: number) => Promise<SmartSearchFeatureHit[]>
  flyToHit: (hit: SmartSearchHit) => void
  flyToLngLat: (lng: number, lat: number) => void
  searchHighlightActive: boolean
  stationExplorationDirty: boolean
  resetStationExploration: () => void
  explorationResetNonce: number
  clearSearchFocus: () => void
  requestFeatureSearch: (query: string) => void
  featureSearchStatus: FeatureSearchStatus
  // --- multi-source results ---
  rasterHits: SmartSearchRasterHit[]
  messageHits: SmartSearchMessageHit[]
  searchTotals: Record<GlobalSearchKind, number>
  // --- filters ---
  filters: SmartSearchFilters
  setFilters: (
    next:
      | SmartSearchFilters
      | ((prev: SmartSearchFilters) => SmartSearchFilters),
  ) => void
  resetFilters: () => void
  filtersActive: boolean
}

export const SmartSearchContext = createContext<SmartSearchContextValue | null>(
  null,
)

type SmartSearchProviderProps = { children: ReactNode }

const DEBOUNCE_MS = 250

export function SmartSearchProvider({ children }: SmartSearchProviderProps) {
  const mapBoxRef = useRef<mapboxgl.Map | null>(null)
  const layerRowsRef = useRef(buildLayerSearchRows([]))
  const lastSearchLayerIdRef = useRef<number | null>(null)
  const lastSearchVisibilitySnapshotRef =
    useRef<OperationalLayerVisibilitySnapshot | null>(null)

  // La bulle de resultat est rendue hors de l'arbre React : elle a besoin du
  // theme courant. flyToHit etant fige par ses deps, on le lit par ref.
  const themeCtx = useSafeTheme()
  const themeCtxRef = useRef(themeCtx)
  themeCtxRef.current = themeCtx

  const [searchHighlightActive, setSearchHighlightActive] = useState(false)
  const [stationExplorationDirty, setStationExplorationDirty] = useState(false)
  const [explorationResetNonce, setExplorationResetNonce] = useState(0)

  const registerMap = useCallback((map: mapboxgl.Map | null) => {
    mapBoxRef.current = map
    if (!map) {
      removeSmartSearchNavigationPin()
      removeSmartSearchHitPopup()
      lastSearchLayerIdRef.current = null
      lastSearchVisibilitySnapshotRef.current = null
      setSearchHighlightActive(false)
      setStationExplorationDirty(false)
    }
  }, [])

  const [layerCategoryFilter, setLayerCategoryFilter] =
    useState<LayerSearchFacetFilters['category']>(null)

  const [folderFacetLabels, setFolderFacetLabels] = useState<string[]>([])
  const [hasLayersWithoutFolder, setHasLayersWithoutFolder] = useState(false)

  const [paletteOpen, setPaletteOpen] = useState(false)
  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const closePalette = useCallback(() => setPaletteOpen(false), [])
  const togglePalette = useCallback(() => setPaletteOpen(o => !o), [])

  const { user, hasPermission, hasModule } = useAuth()
  const resortId = user?.resort_id

  const canViewOperational = useMemo(
    () => hasPermission('gis_operational:view'),
    [hasPermission],
  )

  const [indexStatus, setIndexStatus] = useState<SmartSearchIndexStatus>('idle')

  // --- Filters state ---
  const [filters, setFiltersState] = useState<SmartSearchFilters>(
    DEFAULT_SMART_SEARCH_FILTERS,
  )
  const setFilters = useCallback(
    (
      next:
        | SmartSearchFilters
        | ((prev: SmartSearchFilters) => SmartSearchFilters),
    ) => {
      setFiltersState(prev => (typeof next === 'function' ? next(prev) : next))
    },
    [],
  )
  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_SMART_SEARCH_FILTERS)
  }, [])
  const filtersActive = useMemo(() => areFiltersActive(filters), [filters])

  // --- Global search state (server-backed, all sources) ---
  const [searchState, setSearchState] = useState<GlobalSearchState>({
    query: '',
    vectorFeatureHits: [],
    rasterHits: [],
    messageHits: [],
    totals: EMPTY_TOTALS,
    status: 'idle',
  })

  // Debounce timer ref + stale-request guard (single key = query+filters)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestRequestKeyRef = useRef<string>('')
  const lastQueryRef = useRef('')
  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const runSearch = useCallback(
    (query: string, currentFilters: SmartSearchFilters) => {
      const requestKey = JSON.stringify({ query, currentFilters })
      latestRequestKeyRef.current = requestKey
      setSearchState(prev => ({ ...prev, status: 'loading' }))

      void (async () => {
        try {
          const data = await globalSearch({
            q: query,
            types: currentFilters.types,
            createdAfter: currentFilters.createdAfter,
            createdBefore: currentFilters.createdBefore,
            creatorId: currentFilters.creatorId,
            rasterSources: currentFilters.rasterSources,
            rasterTypes: currentFilters.rasterTypes,
          })
          if (latestRequestKeyRef.current !== requestKey) return

          const vectorFeatureHits = []
          const rasterHits: SmartSearchRasterHit[] = []
          const messageHits: SmartSearchMessageHit[] = []
          for (const h of data.hits) {
            if (h.kind === 'vector_feature') {
              vectorFeatureHits.push({
                feature_id: h.feature_id,
                layer_id: h.layer_id,
                geometry: h.geometry,
                properties: h.properties,
              })
            } else if (h.kind === 'raster') {
              // LiDAR rasters are gated behind the snow_depth_lidar module.
              // Socle GIS uploads (gis_upload) are always allowed here.
              if (
                h.source === 'lidar_pipeline' &&
                !hasModule('snow_depth_lidar')
              ) {
                continue
              }
              rasterHits.push({
                kind: 'raster',
                key: `raster-${h.raster_id}`,
                rasterId: h.raster_id,
                name: h.name ?? h.file_name ?? `Raster ${h.raster_id}`,
                rasterType: h.raster_type,
                source: h.source,
                createdAt: h.created_at,
              })
            } else if (h.kind === 'message') {
              messageHits.push({
                kind: 'message',
                key: `message-${h.marker_id}`,
                markerId: h.marker_id,
                publicId: h.public_id,
                description: h.description,
                category: h.category,
                latitude: h.latitude,
                longitude: h.longitude,
                createdAt: h.created_at,
                creatorUserId: h.created_by_user_id,
                creatorUsername: h.creator_username,
              })
            }
          }

          setSearchState({
            query,
            vectorFeatureHits,
            rasterHits,
            messageHits,
            totals: data.totals ?? EMPTY_TOTALS,
            status: 'ready',
          })
        } catch (err) {
          if (latestRequestKeyRef.current !== requestKey) return
          console.error('[smart-search] Global search failed', err)
          setSearchState({
            query,
            vectorFeatureHits: [],
            rasterHits: [],
            messageHits: [],
            totals: EMPTY_TOTALS,
            status: 'error',
          })
        }
      })()
    },
    [hasModule],
  )

  const requestFeatureSearch = useCallback(
    (query: string) => {
      lastQueryRef.current = query
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      const currentFilters = filtersRef.current
      const parsed = parseAttributeQuery(query)
      const hasText = hasPositiveTerms(parsed)
      const hasFilterIntent = areFiltersActive(currentFilters)

      // browse mode (no text and no narrowing filters): nothing to fetch on top
      // of the locally-cached layer index.
      if (!hasText && !hasFilterIntent) {
        latestRequestKeyRef.current = JSON.stringify({ query, currentFilters })
        setSearchState({
          query,
          vectorFeatureHits: [],
          rasterHits: [],
          messageHits: [],
          totals: EMPTY_TOTALS,
          status: 'idle',
        })
        return
      }

      debounceTimerRef.current = setTimeout(() => {
        runSearch(query, currentFilters)
      }, DEBOUNCE_MS)
    },
    [runSearch],
  )

  // re-fetch when filters change, using the latest palette query (not
  // searchState.query which can lag behind during debounce).
  useEffect(() => {
    requestFeatureSearch(lastQueryRef.current)
  }, [filters, requestFeatureSearch])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // index source: GET /operational-layers/resort/:id (metadata only, no data)
  const loadIndex = useCallback(async () => {
    if (resortId == null) {
      layerRowsRef.current = buildLayerSearchRows([])
      setFolderFacetLabels([])
      setHasLayersWithoutFolder(false)
      setIndexStatus('idle')
      return
    }

    if (!canViewOperational) {
      layerRowsRef.current = buildLayerSearchRows([])
      setFolderFacetLabels([])
      setHasLayersWithoutFolder(false)
      setIndexStatus('no_permission')
      return
    }

    setIndexStatus('loading')

    try {
      const layers = (await getOperationalLayersByResort(
        resortId,
      )) as OperationalLayerRow[]
      const layersSafe = layers ?? []
      const builtRows = buildLayerSearchRows(layersSafe)
      layerRowsRef.current = builtRows

      const names = new Set<string>()
      let noFolder = false
      for (const r of builtRows) {
        if (!r.category) noFolder = true
        else names.add(r.category)
      }
      setFolderFacetLabels(
        [...names].sort((a, b) =>
          a.localeCompare(b, 'fr', { sensitivity: 'base' }),
        ),
      )
      setHasLayersWithoutFolder(noFolder)

      setIndexStatus('ready')
    } catch (error: unknown) {
      layerRowsRef.current = buildLayerSearchRows([])
      setFolderFacetLabels([])
      setHasLayersWithoutFolder(false)
      if (isAxiosError(error) && error.response?.status === 403) {
        setIndexStatus('forbidden')
      } else {
        console.error('[smart-search] Failed to load operational layers', error)
        setIndexStatus('error')
      }
    }
  }, [canViewOperational, resortId])

  useEffect(() => {
    void loadIndex()
  }, [loadIndex])

  const searchLayerGroups = useCallback(
    (query: string) => {
      if (indexStatus !== 'ready') return []
      const facets: LayerSearchFacetFilters = {
        category: layerCategoryFilter,
      }
      const hits =
        searchState.query === query ? searchState.vectorFeatureHits : []
      return buildGroupsFromServerHits(
        query,
        hits,
        layerRowsRef.current,
        facets,
        { creatorId: filters.creatorId },
      )
    },
    [indexStatus, layerCategoryFilter, searchState, filters.creatorId],
  )

  // feature count from the locally-cached operational layer index (metadata).
  const getLayerFeatureCount = useCallback((layerId: number): number => {
    const row = layerRowsRef.current.find(lr => lr.layerId === layerId)
    return row?.featureCount ?? 0
  }, [])

  // layer name from the same index — used to title the map feature popup.
  const getLayerName = useCallback((layerId: number): string | null => {
    const row = layerRowsRef.current.find(lr => lr.layerId === layerId)
    return row?.layerName ?? null
  }, [])

  // attribute order from the same index — the popup orders its rows with it.
  const getLayerPropertiesOrder = useCallback(
    (layerId: number): string[] | null => {
      const row = layerRowsRef.current.find(lr => lr.layerId === layerId)
      return row?.propertiesOrder ?? null
    },
    [],
  )

  // Le dock attributaire persiste un nouvel ordre : le pop-up le lit ici, et
  // rien ne l'en avertissait avant le prochain chargement de page.
  useEffect(() => {
    const surReordonnancement = (evenement: Event) => {
      const detail = (evenement as CustomEvent).detail
      const layerId = Number(detail?.layerId)
      if (!Number.isFinite(layerId)) return
      const ordre = Array.isArray(detail?.propertiesOrder)
        ? (detail.propertiesOrder as string[])
        : null
      layerRowsRef.current = layerRowsRef.current.map(lr =>
        lr.layerId === layerId ? { ...lr, propertiesOrder: ordre } : lr,
      )
    }
    window.addEventListener('gis-layer-order-changed', surReordonnancement)
    return () =>
      window.removeEventListener('gis-layer-order-changed', surReordonnancement)
  }, [])

  const listFeatureHitsForLayer = useCallback(
    async (layerId: number): Promise<SmartSearchFeatureHit[]> => {
      try {
        const geojson = await getLayerFeaturesGeojson(layerId)
        const fc = geojson?.type === 'FeatureCollection' ? geojson : null
        if (!fc?.features) return []

        const layerRow = layerRowsRef.current.find(lr => lr.layerId === layerId)

        return fc.features.map(
          (f: GeoJSON.Feature, i: number): SmartSearchFeatureHit =>
            serverHitToFeatureHit(
              {
                feature_id: f.id ?? `${layerId}-${i}`,
                layer_id: layerId,
                geometry: f.geometry,
                properties:
                  (f.properties as Record<string, unknown> | null) ?? {},
              },
              layerRow,
              `${layerId}-browse-${i}`,
            ),
        )
      } catch (err) {
        console.error(
          '[smart-search] Failed to list features for layer',
          layerId,
          err,
        )
        return []
      }
    },
    [],
  )

  const flyToLngLat = useCallback(
    (lng: number, lat: number) => {
      const map = mapBoxRef.current
      if (!map) return

      removeSmartSearchHitPopup()
      lastSearchVisibilitySnapshotRef.current = null
      lastSearchLayerIdRef.current = null
      setSearchHighlightActive(false)

      const run = () => {
        setSmartSearchNavigationPin(map, lng, lat)
        animateMapToLngLat(map, lng, lat)
      }

      if (!map.isStyleLoaded()) {
        map.once('style.load', run)
      } else {
        run()
      }
      setStationExplorationDirty(true)
      closePalette()
    },
    [closePalette],
  )

  const flyToHit = useCallback(
    (hit: SmartSearchHit) => {
      const map = mapBoxRef.current
      if (!map) return

      removeSmartSearchNavigationPin()
      if (hit.kind === 'feature' || hit.kind === 'layer') {
        lastSearchVisibilitySnapshotRef.current =
          snapshotOperationalLayerVisibility(map, hit.layerId)
        focusSmartSearchHit(
          map,
          hit,
          themeCtxRef.current,
          getLayerPropertiesOrder(hit.layerId),
        )
        lastSearchLayerIdRef.current = hit.layerId
        setSearchHighlightActive(true)
      } else if (hit.kind === 'message') {
        animateMapToLngLat(map, hit.longitude, hit.latitude)
      } else if (hit.kind === 'raster') {
        if (hit.source === 'lidar_pipeline') {
          dispatchLayerFlyTo(hit.rasterId)
        } else {
          dispatchGisRasterFlyTo(hit.rasterId)
        }
      }
      setStationExplorationDirty(true)
      closePalette()
    },
    [closePalette, getLayerPropertiesOrder],
  )

  const clearSearchFocus = useCallback(() => {
    const map = mapBoxRef.current
    const snapshot = lastSearchVisibilitySnapshotRef.current
    removeSmartSearchNavigationPin()
    if (map && snapshot && snapshot.size > 0) {
      restoreOperationalLayerVisibility(map, snapshot)
    }
    removeSmartSearchHitPopup()
    lastSearchVisibilitySnapshotRef.current = null
    lastSearchLayerIdRef.current = null
    setSearchHighlightActive(false)
  }, [])

  const resetStationExploration = useCallback(() => {
    clearSearchFocus()
    const map = mapBoxRef.current
    if (map) void restoreStationExplorationView(map)
    closePalette()
    setStationExplorationDirty(false)
    setExplorationResetNonce(n => n + 1)
  }, [clearSearchFocus, closePalette])

  const value = useMemo<SmartSearchContextValue>(
    () => ({
      registerMap,
      paletteOpen,
      openPalette,
      closePalette,
      togglePalette,
      indexStatus,
      refreshIndex: loadIndex,
      layerCategoryFilter,
      setLayerCategoryFilter,
      folderFacetLabels,
      hasLayersWithoutFolder,
      searchLayerGroups,
      getLayerFeatureCount,
      getLayerName,
      getLayerPropertiesOrder,
      listFeatureHitsForLayer,
      flyToHit,
      flyToLngLat,
      searchHighlightActive,
      stationExplorationDirty,
      resetStationExploration,
      explorationResetNonce,
      clearSearchFocus,
      requestFeatureSearch,
      featureSearchStatus: searchState.status,
      rasterHits: searchState.rasterHits,
      messageHits: searchState.messageHits,
      searchTotals: searchState.totals,
      filters,
      setFilters,
      resetFilters,
      filtersActive,
    }),
    [
      registerMap,
      paletteOpen,
      openPalette,
      closePalette,
      togglePalette,
      indexStatus,
      loadIndex,
      layerCategoryFilter,
      folderFacetLabels,
      hasLayersWithoutFolder,
      searchLayerGroups,
      getLayerFeatureCount,
      getLayerName,
      getLayerPropertiesOrder,
      listFeatureHitsForLayer,
      flyToHit,
      flyToLngLat,
      searchHighlightActive,
      stationExplorationDirty,
      resetStationExploration,
      explorationResetNonce,
      clearSearchFocus,
      requestFeatureSearch,
      searchState,
      filters,
      setFilters,
      resetFilters,
      filtersActive,
    ],
  )

  return (
    <SmartSearchContext.Provider value={value}>
      {children}
    </SmartSearchContext.Provider>
  )
}
